import { useCallback, useEffect, useMemo, useState } from "react";

export interface Job {
  id: string;
  /** job / project name shown everywhere */
  name: string;
  /** optional job number as it appears on the paperwork */
  number: string;
  /** optional customer or general contractor */
  client: string;
  createdAt: number;
  updatedAt: number;
}

const JOBS_KEY = "tt-jobs";
const ACTIVE_KEY = "tt-active-job";

function readJobs(): Job[] {
  try {
    const raw = localStorage.getItem(JOBS_KEY);
    const list = raw ? (JSON.parse(raw) as Job[]) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

const newId = () =>
  `job-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

/**
 * Jobs are plain records on the device — every sketch, spool roll-up and
 * estimate can be filed under one so a crew can juggle several projects
 * without a network.
 */
export function useJobs() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setJobs(readJobs());
    try {
      setActiveJobId(localStorage.getItem(ACTIVE_KEY));
    } catch {
      /* storage unavailable */
    }
    setLoaded(true);
  }, []);

  const persist = useCallback((list: Job[]) => {
    setJobs(list);
    try {
      localStorage.setItem(JOBS_KEY, JSON.stringify(list));
    } catch {
      /* storage full — keep in memory */
    }
  }, []);

  useEffect(() => {
    if (!loaded) return;
    try {
      if (activeJobId) localStorage.setItem(ACTIVE_KEY, activeJobId);
      else localStorage.removeItem(ACTIVE_KEY);
    } catch {
      /* ignore */
    }
  }, [activeJobId, loaded]);

  const addJob = useCallback(
    (name?: string) => {
      const now = Date.now();
      const job: Job = {
        id: newId(),
        name: (name ?? "").trim() || `Job ${readJobs().length + 1}`,
        number: "",
        client: "",
        createdAt: now,
        updatedAt: now,
      };
      persist([job, ...readJobs()]);
      setActiveJobId(job.id);
      return job;
    },
    [persist],
  );

  const updateJob = useCallback(
    (id: string, patch: Partial<Pick<Job, "name" | "number" | "client">>) => {
      persist(
        readJobs().map((j) =>
          j.id === id ? { ...j, ...patch, updatedAt: Date.now() } : j,
        ),
      );
    },
    [persist],
  );

  const touchJob = useCallback(
    (id: string) => {
      persist(readJobs().map((j) => (j.id === id ? { ...j, updatedAt: Date.now() } : j)));
    },
    [persist],
  );

  const removeJob = useCallback(
    (id: string) => {
      persist(readJobs().filter((j) => j.id !== id));
      setActiveJobId((cur) => (cur === id ? null : cur));
    },
    [persist],
  );

  const activeJob = useMemo(
    () => jobs.find((j) => j.id === activeJobId) ?? null,
    [jobs, activeJobId],
  );

  return {
    jobs,
    activeJobId,
    activeJob,
    setActiveJobId,
    addJob,
    updateJob,
    touchJob,
    removeJob,
  };
}
